import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { MAX_PAGINATION_SIZE } from 'helpers/globalConstants';
import useInventoryData from 'screens/Inventory/helpers/useInventoryData';
import { ReportsContext } from 'screens/Reports/context/ReportsProvider';
import {
  getProductListFilter,
  getProductCount
} from 'screens/Reports/helpers/helperFunctions';
import {
  ReportCategories,
  ReportSubTypes
} from 'screens/Reports/hooks/useReportMetadata';
import useTaskProducts from 'screens/Reports/hooks/useTaskProducts';
import _ from 'lodash';
import GroupByProducts from './components/GroupByProducts';

const removeDuplicateProducts = _products => [
  ...new Map(_products.map(_p => [_p.productId, _p])).values()
];

const ProductsSection = ({ cropSeasons }) => {
  const [products, setProducts] = useState([]);
  const { values } = useFormikContext();
  const { getActiveReport } = useContext(ReportsContext);
  const activeReport = getActiveReport();

  const includeInvoiceProducts =
    activeReport.category === ReportCategories.Products &&
    activeReport.subType === ReportSubTypes.InventorySummary;

  const payload = () => {
    return values.selectedCropSeasons
      ? {
          seasonIdsCommaSeparated: values.selectedCropSeasons
            .map(cs => cs.id)
            .join(',')
        }
      : {};
  };

  const {
    taskProducts,
    loading: isTaskProductsLoading,
    error: taskProductsError, // Destructure error state
    selectedCropSeasonIds,
    setSelectedCropSeasonIds
  } = useTaskProducts(payload());
  const {
    products: inventoryProducts,
    loading: isInventoryLoading,
    error: inventoryError, // Destructure error state
    getInventoryDetails
  } = useInventoryData(false, true);

  useEffect(() => {
    if (values.selectedCropSeasons.length && activeReport) {
      const currentSelectedCropSeasonIds = values.selectedCropSeasons.map(
        cs => cs.id
      );
      if (!_.isEqual(currentSelectedCropSeasonIds, selectedCropSeasonIds)) {
        setSelectedCropSeasonIds(values.selectedCropSeasons.map(cs => cs.id));
      }
      if (includeInvoiceProducts) {
        getInventoryDetails(undefined, MAX_PAGINATION_SIZE);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.selectedCropSeasons, activeReport]);

  useEffect(() => {
    if (
      !values.selectedCropSeasons.length ||
      !taskProducts ||
      isTaskProductsLoading ||
      (includeInvoiceProducts && (!inventoryProducts || isInventoryLoading))
    )
      return;
    let displayProducts = taskProducts;
    if (includeInvoiceProducts) {
      displayProducts = displayProducts.concat(
        inventoryProducts.filter(p => p.invoicesIds?.length)
      );
      displayProducts = removeDuplicateProducts(displayProducts);
    }
    displayProducts.sort((a, b) => a.productName.localeCompare(b.productName));
    setProducts(globalThis.structuredClone(displayProducts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    taskProducts,
    isTaskProductsLoading,
    inventoryProducts,
    isInventoryLoading,
    values.selectedCropSeasons
  ]);

  const isLoadingProducts =
    isTaskProductsLoading || (includeInvoiceProducts && isInventoryLoading);

  const formattedData = useMemo(() => {
    if (taskProductsError || inventoryError || isLoadingProducts) return [];
    const filtered = getProductListFilter(products);
    if (!filtered?.length) return [];
    return filtered;
  }, [taskProductsError, inventoryError, isLoadingProducts, products]);

  if (!cropSeasons?.length) {
    return null;
  }

  return (
    <div className="flex flex-col bg-white h-10 w-full">
      <h1
        data-testid="product-text"
        className="font-body font-semibold text-xl"
        style={{ color: '#14151C' }}
      >
        Products
        {isLoadingProducts ? null : ` (${getProductCount(formattedData)})`}
      </h1>
      <GroupByProducts
        mappedData={isLoadingProducts ? null : formattedData}
        loading={isLoadingProducts}
      />
    </div>
  );
};

ProductsSection.defaultProps = {
  cropSeasons: []
};

ProductsSection.propTypes = {
  cropSeasons: PropTypes.arrayOf(PropTypes.object)
};

export default ProductsSection;
